import { flow, pipe } from 'fp-ts/lib/function';
import { OptionSetForm } from '@/entities/optionSet';
import { chain, map } from 'fp-ts/lib/TaskEither';
import { detailCodec, formCodec, listCodec } from './codec';
import {
  addVersionForUpdate,
  createAuthorizedDeleteRequest,
  createAuthorizedGetRequest,
  createAuthorizedPostRequest,
  createAuthorizedPutRequest,
  decode,
  decodeId,
  decodeVersioned,
  fetchApi,
  setBody,
  setQuery,
  Versioned,
} from '../..';
import { OptionSetQuery } from './model';
import { encodeOptionSetQuery } from './query';
import { resourceBulkDelete } from '@/services/api/resources/bulkDelete';

const baseUrl = '/v2/option-sets';
const recordUrl = (id: number): string => `${baseUrl}/${id}`;

export const list = (query: OptionSetQuery) => pipe(
  baseUrl,
  createAuthorizedGetRequest,
  map(setQuery(encodeOptionSetQuery(query))),
  chain(fetchApi),
  chain(decode(listCodec)),
);

export const get = flow(
  recordUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(detailCodec)),
);

export const create = (optionSet: OptionSetForm) => pipe(
  baseUrl,
  createAuthorizedPostRequest,
  map(setBody(formCodec.encode(optionSet))),
  chain(fetchApi),
  chain(decodeId),
);

export const update = (id: number, optionSet: Versioned<OptionSetForm>) => pipe(
  id,
  recordUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(optionSet.version)),
  map(setBody(formCodec.encode(optionSet.data))),
  chain(fetchApi),
);

export const remove = (id: number, propertyId?: number | null) => pipe(
  id,
  recordUrl,
  createAuthorizedDeleteRequest,
  map(req => propertyId ? setQuery({ propertyId: propertyId.toString() })(req) : req),
  chain(fetchApi),
);

export const bulkDelete = (records: number[]) => resourceBulkDelete(records, baseUrl);
